import { trackEvent } from '@/entities/sogu';
import { computed, type useShadowRoot, watch } from 'vue';
import { useBootstrapExperiments } from '../useBootstrapExperiments.ts';
import { useScreenSize } from '@experience/shared';
import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';

export const useExcursionCalendarFormatExperiment = () => {
  const EXP_NAME = 'exp_listing_popup_expfmt_add';
  const TARGET_EXP_VARIANT = 'exp_listing_popup_expfmt_add';
  const { isExperimentVariantActive } = useBootstrapExperiments();

  const { isMobile } = useScreenSize();
  const filtersStore = useExcursionFiltersStore();

  const isExpActive = computed(() => {
    return isMobile.value && isExperimentVariantActive(EXP_NAME, TARGET_EXP_VARIANT);
  });

  watch(
    () => isExpActive.value,
    (value) => {
      if (value === undefined) {
        return;
      }

      trackEvent('experiments.hit', {
        experiment: EXP_NAME,
        variant: value ? TARGET_EXP_VARIANT : 'original',
      });
    },
    { immediate: true, deep: true },
  );

  const setExperimentType = () => {
    const isActiveFilterType = filtersStore.state.type?.length;
    if (isExpActive.value && !isActiveFilterType) {
      filtersStore.state.type = ['private,tour_private', 'group,tour_group'];
    }
  };

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const isExpTitle = ref(true);

  const observer = ref<IntersectionObserver | null>(null);

  const initializeFilterObserver = () => {
    if (observer.value) {
      observer.value.disconnect();
    }
    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            isExpTitle.value = true;
          } else {
            isExpTitle.value = false;
          }
        });
      },
      { threshold: 0.1, rootMargin: '-30px 0px 0px 0px' },
    );

    nextTick(() => {
      const filterSection = rootNode?.getElementById('experiment-block');
      if (filterSection && observer.value) {
        observer.value.observe(filterSection);
      }
    });
  };

  onMounted(() => {
    setExperimentType();
    initializeFilterObserver();
  });

  onUnmounted(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });

  return { isExpActive, setExperimentType, isExpTitle };
};
